import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import { PresthygCard, PresthygPageHeader, PresthygButton } from '../components/presthyg';
import { Plus, Edit2, Trash2, Save, X, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';

const BACKEND = BACKEND_BASE;

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', address: '', city: '', contact_person: '', phone: '', email: '', notes: '', logo: '',
  });

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    try {
      const { data } = await api.get('/clients');
      setClients(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['png', 'jpg', 'jpeg'].includes(ext)) {
      toast.error('Format non supporte (PNG ou JPG)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/clients/upload-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({ ...prev, logo: data.url }));
      toast.success('Logo uploade');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/clients/${editingId}`, formData);
        toast.success('Client modifie');
      } else {
        await api.post('/clients', formData);
        toast.success('Client cree');
      }
      resetForm();
      loadClients();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (client) => {
    setFormData({
      name: client.name, address: client.address || '', city: client.city || '',
      contact_person: client.contact_person || '', phone: client.phone || '',
      email: client.email || '', notes: client.notes || '', logo: client.logo || '',
    });
    setEditingId(client.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Client supprime');
      loadClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', city: '', contact_person: '', phone: '', email: '', notes: '', logo: '' });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return <Layout><div className="flex items-center justify-center py-24"><div className="w-8 h-8 border-3 border-t-transparent animate-spin" style={{ borderColor: 'var(--ph-teal)', borderTopColor: 'transparent', borderRadius: '50%', borderWidth: '3px' }} /></div></Layout>;

  return (
    <Layout>
      <div data-testid="clients-page">
        <PresthygPageHeader
          title="Clients"
          subtitle={`${clients.length} client(s)`}
          actions={!showForm && (
            <PresthygButton variant="teal" onClick={() => setShowForm(true)} data-testid="add-client-button">
              <Plus size={16} /> Ajouter un client
            </PresthygButton>
          )}
        />

        {showForm && (
          <PresthygCard className="p-6 mb-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold" style={{ color: 'var(--ph-navy)', fontFamily: 'var(--font-display)' }}>
                {editingId ? 'Modifier le client' : 'Nouveau client'}
              </h3>
              <button onClick={resetForm} className="p-1.5 rounded-full hover:bg-gray-100"><X size={18} style={{ color: 'var(--ph-slate500)' }} /></button>
            </div>
            <form onSubmit={handleSubmit} data-testid="client-form">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ph-navy)' }}>Nom *</label>
                  <input type="text" value={formData.name} required
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="client-name-input"
                    className="w-full px-4 py-2.5 border text-sm" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ph-navy)' }}>Ville</label>
                  <input type="text" value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    data-testid="client-city-input"
                    className="w-full px-4 py-2.5 border text-sm" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ph-navy)' }}>Personne de contact</label>
                  <input type="text" value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    data-testid="client-contact-input"
                    className="w-full px-4 py-2.5 border text-sm" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ph-navy)' }}>Telephone</label>
                  <input type="text" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="client-phone-input"
                    className="w-full px-4 py-2.5 border text-sm" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }} />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ph-navy)' }}>Adresse</label>
                  <input type="text" value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    data-testid="client-address-input"
                    className="w-full px-4 py-2.5 border text-sm" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }} />
                </div>

                {/* Logo upload */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ph-navy)' }}>Logo du client</label>
                  <div className="flex items-center gap-4">
                    {formData.logo ? (
                      <div className="relative">
                        <img src={`${BACKEND}${formData.logo}`} alt="Logo" className="w-16 h-16 object-contain border" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-sm)' }} />
                        <button type="button" onClick={() => setFormData({ ...formData, logo: '' })}
                          className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full" style={{ backgroundColor: '#EF4444', color: '#fff' }}>
                          <X size={10} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 flex items-center justify-center border-2 border-dashed" style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-sm)', backgroundColor: 'var(--ph-slate50)' }}>
                        <ImageIcon size={20} style={{ color: 'var(--ph-slate500)' }} />
                      </div>
                    )}
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border text-sm font-medium transition-all hover:bg-gray-50"
                      style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-pill)', color: 'var(--ph-navy)' }}>
                      <Upload size={14} />
                      {uploading ? 'Upload...' : 'Choisir un logo'}
                      <input type="file" accept=".png,.jpg,.jpeg" onChange={handleLogoUpload} className="hidden" data-testid="client-logo-input" />
                    </label>
                    <span className="text-xs" style={{ color: 'var(--ph-slate500)' }}>PNG ou JPG, max 5MB</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <PresthygButton type="submit" variant="teal" data-testid="save-client-button">
                  <Save size={16} /> Enregistrer
                </PresthygButton>
                <PresthygButton type="button" variant="secondary" onClick={resetForm}>Annuler</PresthygButton>
              </div>
            </form>
          </PresthygCard>
        )}

        <PresthygCard className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--ph-slate50)' }}>
                <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--ph-slate500)' }}>Logo</th>
                <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--ph-slate500)' }}>Nom</th>
                <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--ph-slate500)' }}>Ville</th>
                <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--ph-slate500)' }}>Contact</th>
                <th className="px-5 py-3 text-left font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--ph-slate500)' }}>Telephone</th>
                <th className="px-5 py-3 text-right font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--ph-slate500)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 ? (
                <tr><td colSpan="6" className="px-5 py-12 text-center" style={{ color: 'var(--ph-slate500)' }}>Aucun client. Ajoutez votre premier client.</td></tr>
              ) : clients.map((client) => (
                <tr key={client.id} data-testid={`client-row-${client.id}`} className="border-t" style={{ borderColor: 'var(--ph-slate200)' }}>
                  <td className="px-5 py-3">
                    {client.logo ? (
                      <img src={`${BACKEND}${client.logo}`} alt="" className="w-10 h-10 object-contain" style={{ borderRadius: 'var(--ph-radius-sm)' }} />
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center" style={{ borderRadius: 'var(--ph-radius-sm)', backgroundColor: 'var(--ph-slate100)' }}>
                        <span className="text-sm font-bold" style={{ color: 'var(--ph-slate500)' }}>{(client.name || 'C')[0]}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3 font-semibold" style={{ color: 'var(--ph-navy)' }}>{client.name}</td>
                  <td className="px-5 py-3" style={{ color: 'var(--ph-slate700)' }}>{client.city}</td>
                  <td className="px-5 py-3" style={{ color: 'var(--ph-slate700)' }}>{client.contact_person}</td>
                  <td className="px-5 py-3" style={{ color: 'var(--ph-slate700)' }}>{client.phone}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => handleEdit(client)} data-testid={`edit-client-${client.id}`}
                        className="p-2 rounded-lg hover:bg-blue-50"><Edit2 size={15} style={{ color: 'var(--ph-teal)' }} /></button>
                      <button onClick={() => handleDelete(client.id)} data-testid={`delete-client-${client.id}`}
                        className="p-2 rounded-lg hover:bg-red-50"><Trash2 size={15} style={{ color: '#DC2626' }} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </PresthygCard>
      </div>
    </Layout>
  );
};

export default Clients;
